<script setup lang="ts">
/**
 * Table cell for a date/time value. Renders a two-line block via
 * `formatDateTime`: the formatted date on the primary line and the formatted
 * time (muted, smaller) on the secondary line. Mirrors the layout of
 * `StartDateCell` and `LastVisitCell` so all two-line date cells are visually
 * consistent.
 */
import { computed } from 'vue'
import { formatDateTime } from '@/utils/formatDateTime'

const props = withDefaults(
  defineProps<{
    /** Date/time (Date instance or ISO string). */
    date: Date | string
    /** Locale for formatting. Default from config. */
    locale?: string
    /** Intl.DateTimeFormat dateStyle: 'long' | 'short' | 'medium'. Default from config. */
    dateStyle?: 'long' | 'short' | 'medium'
    /** Intl.DateTimeFormat timeStyle for the time line: 'short' | 'medium' | 'long'. */
    timeStyle?: 'short' | 'medium' | 'long'
    /** IANA timezone for display. Undefined = local timezone. Default from config. */
    timeZone?: string
    /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
    maxWidth?: string
  }>(),
  {
    locale: undefined,
    dateStyle: undefined,
    timeStyle: 'short',
    timeZone: undefined,
  },
)

const result = computed(() =>
  formatDateTime({
    date: props.date,
    locale: props.locale,
    dateStyle: props.dateStyle,
    timeStyle: props.timeStyle,
    timeZone: props.timeZone,
  }),
)
</script>

<template>
  <td
    class="font-tufte text-tufte-text"
    :style="props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <div class="flex flex-col gap-0.5">
      <span>{{ result.formattedDate }}</span>
      <span class="text-tufte-muted" style="font-size: var(--text-tufte-xs)">
        {{ result.formattedTime }}
      </span>
    </div>
  </td>
</template>
